<template>
  <div class="space-y-3">
    <!-- Title -->
    <h2 class="text-base font-semibold text-slate-800 dark:text-white">
      {{ mode === 'queue' ? `📊 ${queueName}` : '📊 Persönliche Statistik' }}
    </h2>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <Loader2 class="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
      <p class="text-slate-500 text-sm">Lade Statistiken...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-12">
      <p class="text-red-500 text-sm">{{ error }}</p>
      <button @click="fetchAnalytics" class="mt-2 text-sm text-blue-600 hover:underline">Erneut versuchen</button>
    </div>

    <!-- Dashboard -->
    <div v-else-if="data" class="space-y-3">
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-3 text-center">
          <p class="text-xl font-bold text-slate-800 dark:text-white">{{ data.summary.totalCalls }}</p>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Gesamt</p>
        </div>
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-3 text-center">
          <p class="text-xl font-bold text-green-600 dark:text-green-400">{{ data.summary.answered }}</p>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Angenommen</p>
        </div>
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-3 text-center">
          <p class="text-xl font-bold text-red-500">{{ data.summary.missed }}</p>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Verpasst ({{ data.summary.missedRate }}%)</p>
        </div>
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-3 text-center">
          <p class="text-xl font-bold text-slate-800 dark:text-white">{{ formatDuration(data.summary.avgDuration) }}</p>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Ø Dauer</p>
        </div>
      </div>

      <!-- Direction Donut + Top Callers / Agent Stats side by side -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <!-- Direction Chart (personal) or Answered vs Missed Donut (queue) -->
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            {{ mode === 'queue' ? 'Angenommen / Verpasst' : 'Richtung' }}
          </h3>
          <div class="flex items-center justify-center gap-8">
            <div class="relative w-32 h-32">
              <canvas ref="directionChart"></canvas>
            </div>
            <div class="space-y-2 text-sm" v-if="mode !== 'queue'">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-blue-500"></span>
                <span class="text-slate-600 dark:text-slate-400">Eingehend: {{ data.summary.inbound }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-emerald-500"></span>
                <span class="text-slate-600 dark:text-slate-400">Ausgehend: {{ data.summary.outbound }}</span>
              </div>
            </div>
            <div class="space-y-2 text-sm" v-else>
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-emerald-500"></span>
                <span class="text-slate-600 dark:text-slate-400">Angenommen: {{ data.summary.answered }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-red-500"></span>
                <span class="text-slate-600 dark:text-slate-400">Verpasst: {{ data.summary.missed }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Agent Stats (queue mode) or Top Callers (personal mode) -->
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
          <!-- Queue mode: Agent performance -->
          <template v-if="mode === 'queue'">
            <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Agenten-Leistung</h3>
            <div v-if="!data.agentStats || data.agentStats.length === 0" class="text-sm text-slate-400 text-center py-4">Keine Agentendaten</div>
            <div v-else class="space-y-2 max-h-48 overflow-y-auto">
              <div
                v-for="(agent, idx) in data.agentStats"
                :key="agent.userId"
                class="flex items-center justify-between text-sm"
              >
                <div class="flex items-center gap-2 min-w-0 flex-1">
                  <span class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                    :class="idx === 0 ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'">
                    {{ idx + 1 }}
                  </span>
                  <p class="text-slate-700 dark:text-slate-300 truncate">{{ agent.name || 'Unbekannt' }}</p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span class="text-xs text-slate-500 dark:text-slate-400">{{ formatDuration(agent.avgDuration) }}</span>
                  <span class="text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                    {{ agent.callsAnswered }}× ({{ agent.percentage }}%)
                  </span>
                </div>
              </div>
            </div>
          </template>
          <!-- Personal mode: Top external callers -->
          <template v-else>
            <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Häufigste externe Anrufer</h3>
            <div v-if="data.topCallers.length === 0" class="text-sm text-slate-400 text-center py-4">Keine externen Anrufe</div>
            <div v-else class="space-y-2 max-h-48 overflow-y-auto">
              <div
                v-for="(caller, idx) in data.topCallers.slice(0, 8)"
                :key="idx"
                class="flex items-center justify-between text-sm"
              >
                <div class="flex items-center gap-2 min-w-0 flex-1">
                  <span class="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-400 flex-shrink-0">
                    {{ idx + 1 }}
                  </span>
                  <div class="min-w-0">
                    <p class="text-slate-700 dark:text-slate-300 truncate">{{ caller.caller }}</p>
                    <p v-if="caller.company" class="text-xs text-slate-400 truncate">{{ caller.company }}</p>
                  </div>
                </div>
                <span class="text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full flex-shrink-0">
                  {{ caller.count }}×
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Daily Trend Chart -->
      <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
        <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Anrufe pro Tag</h3>
        <div class="h-36">
          <canvas ref="dailyChart"></canvas>
        </div>
      </div>

      <!-- Hourly + Weekday Charts side by side -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <!-- Hourly -->
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Anrufe nach Uhrzeit</h3>
          <div class="h-32">
            <canvas ref="hourlyChart"></canvas>
          </div>
        </div>

        <!-- Weekday -->
        <div class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Anrufe nach Wochentag</h3>
          <div class="h-32">
            <canvas ref="weekdayChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Queue Stats (personal mode only — overview of all queues) -->
      <div v-if="mode !== 'queue' && data.queueStats && data.queueStats.length > 0" class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
        <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Call Queue Übersicht</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200/50 dark:border-slate-700/50">
                <th class="pb-2 font-medium">Queue</th>
                <th class="pb-2 font-medium text-right">Gesamt</th>
                <th class="pb-2 font-medium text-right">Verpasst</th>
                <th class="pb-2 font-medium text-right">Verpasst %</th>
                <th class="pb-2 font-medium text-right">Ø Dauer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="q in data.queueStats" :key="q.name" class="border-b border-slate-100/50 dark:border-slate-700/30 last:border-0">
                <td class="py-2 text-slate-700 dark:text-slate-300 font-medium">{{ q.name }}</td>
                <td class="py-2 text-right text-slate-600 dark:text-slate-400">{{ q.total }}</td>
                <td class="py-2 text-right text-red-500">{{ q.missed }}</td>
                <td class="py-2 text-right">
                  <span 
                    class="px-1.5 py-0.5 rounded-full text-xs font-medium"
                    :class="(q.missed / q.total * 100) > 20 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'"
                  >
                    {{ q.total > 0 ? Math.round(q.missed / q.total * 100) : 0 }}%
                  </span>
                </td>
                <td class="py-2 text-right text-slate-600 dark:text-slate-400">{{ formatDuration(q.avgDuration) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Top external callers in queue mode (below charts) -->
      <div v-if="mode === 'queue' && data.topCallers && data.topCallers.length > 0" class="bg-white/90 dark:bg-slate-800/90 border border-white/20 dark:border-slate-700/50 rounded-2xl p-4">
        <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Häufigste externe Anrufer</h3>
        <div class="space-y-2 max-h-48 overflow-y-auto">
          <div
            v-for="(caller, idx) in data.topCallers.slice(0, 8)"
            :key="idx"
            class="flex items-center justify-between text-sm"
          >
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <span class="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-400 flex-shrink-0">
                {{ idx + 1 }}
              </span>
              <div class="min-w-0">
                <p class="text-slate-700 dark:text-slate-300 truncate">{{ caller.caller }}</p>
                <p v-if="caller.company" class="text-xs text-slate-400 truncate">{{ caller.company }}</p>
              </div>
            </div>
            <span class="text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full flex-shrink-0">
              {{ caller.count }}×
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data -->
    <div v-else class="text-center py-12">
      <p class="text-slate-400 text-sm">Keine Daten verfügbar</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onUnmounted } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { authenticatedFetch } from '../oauth2-config';
import { Chart, DoughnutController, BarController, LineController, ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip, Legend } from 'chart.js';

Chart.register(DoughnutController, BarController, LineController, ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip, Legend);

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const props = defineProps({
  mode: { type: String, default: 'personal' },       // 'personal' or 'queue'
  queueEmail: { type: String, default: '' },          // queue email (for queue mode)
  queueName: { type: String, default: '' },           // queue display name
  days: { type: Number, default: 30 },                // time period in days (from parent)
});

const loading = ref(false);
const error = ref(null);
const data = ref(null);

// Chart refs
const directionChart = ref(null);
const dailyChart = ref(null);
const hourlyChart = ref(null);
const weekdayChart = ref(null);

// Chart instances (for cleanup)
let directionChartInstance = null;
let dailyChartInstance = null;
let hourlyChartInstance = null;
let weekdayChartInstance = null;

const isDark = () => document.documentElement.classList.contains('dark');

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
};

const destroyCharts = () => {
  directionChartInstance?.destroy();
  dailyChartInstance?.destroy();
  hourlyChartInstance?.destroy();
  weekdayChartInstance?.destroy();
  directionChartInstance = null;
  dailyChartInstance = null;
  hourlyChartInstance = null;
  weekdayChartInstance = null;
};

const fetchAnalytics = async () => {
  loading.value = true;
  error.value = null;
  try {
    let url = `${API_URL}/api/calls/analytics?days=${props.days || 30}`;
    if (props.mode === 'queue' && props.queueEmail) {
      url += `&mode=queue&queueEmail=${encodeURIComponent(props.queueEmail)}`;
    } else {
      url += '&mode=personal';
    }
    const response = await authenticatedFetch(url);
    if (!response.ok) throw new Error('Failed to fetch analytics');
    data.value = await response.json();
    // IMPORTANT: Set loading=false BEFORE nextTick so that v-else-if="data" renders
    // the canvas elements before renderCharts() tries to access them
    loading.value = false;
    await nextTick();
    renderCharts();
  } catch (e) {
    console.error('Analytics fetch failed:', e);
    error.value = 'Statistiken konnten nicht geladen werden';
    loading.value = false;
  }
};

// Watch for mode/queue/days changes to re-fetch
watch(() => [props.mode, props.queueEmail, props.days], () => {
  destroyCharts();
  fetchAnalytics();
}, { immediate: true });

const renderCharts = () => {
  if (!data.value) return;
  destroyCharts();
  
  const dark = isDark();
  const gridColor = dark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)';
  const textColor = dark ? '#94a3b8' : '#64748b';

  // Direction Donut (personal) or Answered/Missed Donut (queue)
  if (directionChart.value) {
    const isQueue = props.mode === 'queue';
    directionChartInstance = new Chart(directionChart.value, {
      type: 'doughnut',
      data: {
        labels: isQueue ? ['Angenommen', 'Verpasst'] : ['Eingehend', 'Ausgehend'],
        datasets: [{
          data: isQueue 
            ? [data.value.summary.answered, data.value.summary.missed]
            : [data.value.summary.inbound, data.value.summary.outbound],
          backgroundColor: isQueue ? ['#10b981', '#ef4444'] : ['#3b82f6', '#10b981'],
          borderWidth: 0,
          cutout: '65%'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: { legend: { display: false }, tooltip: { enabled: true } }
      }
    });
  }

  // Daily Trend
  if (dailyChart.value) {
    const dailyLabels = data.value.daily.map(d => {
      const date = new Date(d.day);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' });
    });

    dailyChartInstance = new Chart(dailyChart.value, {
      type: 'line',
      data: {
        labels: dailyLabels,
        datasets: [
          {
            label: 'Anrufe',
            data: data.value.daily.map(d => d.count),
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: (props.days || 30) <= 30 ? 3 : 0,
            pointHoverRadius: 5
          },
          {
            label: 'Verpasst',
            data: data.value.daily.map(d => d.missed),
            borderColor: '#ef4444',
            backgroundColor: 'rgba(239, 68, 68, 0.05)',
            fill: true,
            tension: 0.3,
            borderWidth: 2,
            pointRadius: (props.days || 30) <= 30 ? 3 : 0,
            pointHoverRadius: 5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: true, position: 'top', labels: { color: textColor, font: { size: 11 }, boxWidth: 12, padding: 15 } },
          tooltip: { enabled: true }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, font: { size: 10 }, maxTicksLimit: 10 }
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: { color: textColor, font: { size: 10 }, stepSize: 1 }
          }
        }
      }
    });
  }

  // Hourly Bar
  if (hourlyChart.value) {
    hourlyChartInstance = new Chart(hourlyChart.value, {
      type: 'bar',
      data: {
        labels: data.value.hourly.map(h => `${h.hour}:00`),
        datasets: [{
          label: 'Anrufe',
          data: data.value.hourly.map(h => h.count),
          backgroundColor: dark ? 'rgba(59, 130, 246, 0.6)' : 'rgba(59, 130, 246, 0.7)',
          borderRadius: 4,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, font: { size: 9 }, maxTicksLimit: 12 }
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: { color: textColor, font: { size: 10 }, stepSize: 1 }
          }
        }
      }
    });
  }

  // Weekday Bar
  if (weekdayChart.value) {
    weekdayChartInstance = new Chart(weekdayChart.value, {
      type: 'bar',
      data: {
        labels: data.value.weekday.map(w => w.day),
        datasets: [{
          label: 'Anrufe',
          data: data.value.weekday.map(w => w.count),
          backgroundColor: dark ? 'rgba(16, 185, 129, 0.6)' : 'rgba(16, 185, 129, 0.7)',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, font: { size: 11 } }
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: { color: textColor, font: { size: 10 }, stepSize: 1 }
          }
        }
      }
    });
  }
};

onUnmounted(() => {
  destroyCharts();
});
</script>
